import { useEntityInstanceLayoutStore } from "@/stores/entity-instance-field-store";
import { useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { ChevronRight } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./ui/command";

interface EntityFieldSelectBaseProps {
  entityType: string;
  hideCurrentField?: boolean;
  portal?: boolean;
  initialField?: string;
  onFieldChange?: (newField: string) => void;
}
export const EntityFieldSelectBase: React.FC<EntityFieldSelectBaseProps> = ({
  entityType,
  portal = false,
  initialField,
  onFieldChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const entityNameForRelationOptions = useMemo(() => {
    const typeWithoutInput = entityType.replace("Input", "");
    // the name depends on the relation type
    if (typeWithoutInput.endsWith("ListData")) {
      return typeWithoutInput.replace("ListData", "");
    } else if (typeWithoutInput.endsWith("Data")) {
      return typeWithoutInput.replace("Data", "");
    } else return typeWithoutInput;
  }, [entityType]);

  const { state, setState, entitySchema } = useEntityInstanceLayoutStore(
    entityNameForRelationOptions,
  );

  // Initialize with the initialField if provided and state is empty
  const displayField = initialField || state?.field || "";

  const handleSelect = (fieldName: string) => {
    setIsOpen(false);
    setState({ field: fieldName });
    onFieldChange?.(fieldName);
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger
        className="group flex items-center gap-1 rounded-md"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <ChevronRight size={14} />
        <p className="text-xs text-muted-foreground group-hover:underline">{displayField}</p>
      </PopoverTrigger>
      <PopoverContent noPortal={!portal} align="start" side="right" className="p-0">
        <Command>
          <CommandInput />
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            <CommandGroup>
              {entitySchema?.fields
                //only show scalars no relations
                .filter((f) => f.kind === "SCALAR" && f.name !== displayField)
                .map((f) => (
                  <CommandItem
                    key={f.name}
                    onSelect={(value: string) => {
                      handleSelect(value);
                    }}
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    value={f.name}
                  >
                    {f.name}
                  </CommandItem>
                ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
